import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Lock, Sparkles, Sun, Moon } from "lucide-react";
import { listDemoUsers, login } from "../api";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";

// Highest-privilege first. A user with multiple roles is shown under their
// top-ranked role only (e.g. Iknoor has CRICKET_HEAD + CAPTAIN → CRICKET_HEAD).
const ROLE_ORDER = [
  "ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD",
  "CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN","PLAYER"
];

function highestRole(roles) {
  for (const r of ROLE_ORDER) if (roles.includes(r)) return r;
  return null;
}

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("ashish@gabruzuniverse.com");
  const [password, setPassword] = useState("gabruz123");
  const [error, setError] = useState("");
  const [demoUsers, setDemoUsers] = useState([]);
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();

  useEffect(() => {
    listDemoUsers()
      .then(setDemoUsers)
      .catch(() => setDemoUsers([])); // tolerate API being down
  }, []);

  // One entry per role: { role, user|null }
  const roleButtons = useMemo(() => {
    const byRole = new Map();
    for (const u of demoUsers) {
      const top = highestRole(u.roles || []);
      if (!top) continue;
      if (!byRole.has(top)) byRole.set(top, u); // first wins
    }
    return ROLE_ORDER.map(role => ({ role, user: byRole.get(role) || null }));
  }, [demoUsers]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    try {
      const r = await login(email, password);
      // Phase 3 §3 — Shell reads this on mount and runs the cinematic.
      // Setting it BEFORE the state flip ensures Shell sees it first paint.
      sessionStorage.setItem("gabruz-just-logged-in", "1");
      onLogin(r.user);
    } catch (err) {
      setError(err.message);
    }
  }

  function pickRole(user) {
    if (!user) return;
    setEmail(user.email);
    setPassword("gabruz123");
    setError("");
  }

  return (
    <div className="login-page">
      {/* Phase 3 — version + theme switchers, top-right of the login screen
          so unauthenticated users can preview either design. */}
      <div className="login-controls">
        <div className="versionSwitch" role="radiogroup" aria-label="UI design version">
          <button type="button" className={version === "v1" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v1")} role="radio" aria-checked={version === "v1"} title="Original UI">V1</button>
          <button type="button" className={version === "v2" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v2")} role="radio" aria-checked={version === "v2"} title="Aurora UI">V2</button>
          <button type="button" className={version === "v3" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v3")} role="radio" aria-checked={version === "v3"} title="Bento UI">V3</button>
        </div>
        <button type="button" className="themeToggle" onClick={toggleTheme} aria-label="Toggle color theme" title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}>
          {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </div>

      <div className="login-bg-card">
        <Sparkles />
        <h3>One Universe</h3>
        <p>Community, cricket, volleyball, events, schedules, availability and squads — every workflow lives in one place.</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="login-card"
      >
        <div className="login-logo-wrap">
          <img src="/logo.png" onError={e => e.currentTarget.style.display = "none"} />
        </div>
        <div className="login-title">
          <span>Gabruz Universe</span>
          <h1>Welcome Back</h1>
          <p>Sign in to manage your community and sports operations.</p>
        </div>

        <form onSubmit={submit} className="login-form">
          <label>Email Address</label>
          <input value={email} onChange={e => setEmail(e.target.value)} />
          <label>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
          {error && <div className="error">{error}</div>}
          <button className="primary" type="submit"><Lock size={18} /> Secure Login</button>
        </form>

        <div className="quick-users">
          <p>Demo Login As · Password <b>gabruz123</b></p>
          <div className="role-grid">
            {roleButtons.map(({ role, user }) => (
              <button
                key={role}
                type="button"
                className="role-btn"
                disabled={!user}
                title={user ? `${user.name} (${user.email})` : "no seeded user for this role"}
                onClick={() => pickRole(user)}
              >
                <span>{role.replace("_", " ")}</span>
                {user && <small>{user.name}</small>}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="login-bg-card">
        <Crown />
        <h3>Role Based</h3>
        <p>President, VP, Finance, Cricket Head, Volleyball Head, Captain, Player and Member — each gets a tailored view.</p>
      </div>
    </div>
  );
}
